import ENV_NAMES from '../../environments/ENV_NAMES';
import { countriesConfig, CountryConfig, DEFAULT_COUNTRY_CODE } from '../shared/i18n/i18n.config';

const getSomfyProUrl = (userCountry: string | undefined, appConfigName: ENV_NAMES): string => {
    const urlConfigKey: keyof CountryConfig =
        appConfigName === ENV_NAMES.PROD ? 'returnToSomfyProUrl' : 'returnToSomfyProPreprodUrl';

    return (
        getI18nConfigPropertyByCountry(userCountry, urlConfigKey) ??
        getI18nConfigPropertyByCountry(DEFAULT_COUNTRY_CODE, urlConfigKey) ??
        ''
    );
};

const getContactPageUrl = (country: string | undefined): string => {
    if (!country) {
        throw new Error('missing_country_error');
    }

    const urlConfigKey = 'returnToContactPage';

    const pageUrl =
        getI18nConfigPropertyByCountry(country, urlConfigKey) ??
        getI18nConfigPropertyByCountry(DEFAULT_COUNTRY_CODE, urlConfigKey);
    if (!pageUrl) {
        throw new Error('country_configuration_error');
    }

    return pageUrl;
};

const FAQ_URLS: Readonly<Record<string, string>> = {
    AL: 'https://www.somfy.gr/customer-support-somfy/faq',
    BG: 'https://www.somfy.bg/assistance/faq',
    KR: 'https://www.somfypro.co.kr/faq',
    SG: 'https://www.somfypro.com.sg/frequently-asked-questions',
    TH: 'https://www.somfy.co.th/th-th/assistance/faq',
    HK: 'https://www.somfy.com.hk/cn-hk/hk-v5/home/assistance/faq',
    TW: 'https://www.somfy.com.hk/cn-hk/hk-v5/home/assistance/faq',
    IN: 'https://www.somfy.co.in/assistance/faq',
    ID: 'https://www.somfypro.com.sg/frequently-asked-questions',
    MY: 'https://www.somfypro.com.sg/frequently-asked-questions',
    VN: 'https://www.somfy.co.th/vi-th/assistance/faq',
    IL: 'https://www.somfy.co.il/support/faq',
    EE: 'https://www.somfybaltics.com/et-ee/tugi-abi/korduma-kippuvad-kuesimused',
    LT: 'https://www.somfybaltics.com/lt-lt/pagalba/dazniausiai-uzduodami-klausimai',
    LV: 'https://www.somfybaltics.com/lv-lv/musu-atbalsts/biezak-uzdotie-jautajumi',
    AE: 'https://www.somfy.me/ar-gc/assistance/faq',
    SA: 'https://www.somfy.me/ar-gc/assistance/faq',
    KW: 'https://www.somfy.me/ar-gc/assistance/faq',
    QA: 'https://www.somfy.me/ar-gc/assistance/faq',
    LB: 'https://www.somfy.com.lb/en-lb/support/faq',
    CY: 'https://www.somfy.com.cy/el-cy/support-assistance/faq',
    EG: 'https://www.somfy.com.eg/ar-eg/assistance/faq',
    JO: 'https://www.somfy.com.eg/ar-eg/assistance/faq',
    TR: 'https://www.somfy.com.tr/destek-ve-iletisim/sikca-sorulan-sorular',
    ZA: 'https://www.somfy.co.za/support-and-assistance/faq',
    MA: 'https://www.somfy.ma/ar-ma/assistance/faq',
    TN: 'https://www.somfy.tn/assistance/faq',
    AU: 'https://www.somfy.com.au/assistance/faq',
    NZ: 'https://www.somfy.com.au/assistance/faq',
    AT: 'https://www.somfypro.at/hilfe',
    BE: 'https://www.somfy.be/fr-be/assistance/faq',
    LU: 'https://www.somfy.be/fr-be/assistance/faq',
    CZ: 'https://www.somfypro.cz/faq-new',
    SK: 'https://www.somfypro.cz/faq-new',
    FR: 'https://www.somfypro.fr/assistance',
    DE: 'https://www.somfypro.de/hilfe',
    GR: 'https://www.somfy.gr/customer-support-somfy/faq',
    HR: 'https://www.somfy.hr/pomoc/najcesce-postavljana-pitanja',
    HU: 'https://www.somfy.hu/tamogatas/gyakran-ismetelt-kerdesek',
    XK: 'https://www.somfy.hu/tamogatas/gyakran-ismetelt-kerdesek',
    RS: 'https://www.somfy.rs/pomoc/najcesca-pitanja',
    BA: 'https://www.somfy.rs/pomoc/najcesca-pitanja',
    IT: 'https://www.somfypro.it/assistenza',
    NL: 'https://www.somfy.nl/ondersteuning/veelgestelde-vragen',
    DK: 'https://www.somfy.dk/support/faq',
    FI: 'https://www.somfy.fi/support/faq',
    SE: 'https://www.somfy.se/support/faq',
    PL: 'https://www.somfy.pl/wsparcie/najczesciej-zadawane-pytania',
    PT: 'https://www.somfy.pt/assistencia/perguntas-mais-frecuentes',
    ES: 'https://www.somfypro.es/asistencia',
    CH: 'https://www.somfy.ch/fr-ch/assistance/faq',
    IE: 'https://www.somfy.co.uk/support/faq',
    GB: 'https://www.somfy.co.uk/support/faq',
    US: 'https://www.somfysystems.com/en-us/support/faq',
    RO: 'https://www.somfy.ro/suport/intrebari-frecvente',
    MD: 'https://www.somfy.ro/suport/intrebari-frecvente',
    NO: 'https://www.somfy.no/support/faq',
};

const DEFAULT_FAQ_COUNTRY_CODE = 'GB';

const getFaqPageUrl = (country: string): string => {
    const upperCountry = country?.toUpperCase();
    return FAQ_URLS[upperCountry] ?? FAQ_URLS[DEFAULT_FAQ_COUNTRY_CODE];
};

const getI18nConfigPropertyByCountry = (countryCode: string | undefined, property: keyof CountryConfig) => {
    const countryLowerCase = countryCode?.toLowerCase();
    const countryConf = countriesConfig.find((country) => country.code.toLowerCase() === countryLowerCase);
    return countryConf?.[property];
};

export { getSomfyProUrl, getContactPageUrl, getFaqPageUrl };
